import { mapState } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { cloneDeep } from 'lodash';
import get from 'lodash/get';
export default {
  name: 'CustomMultiSearch',
  props: {
    dense: {
      type: Boolean,
      default: false,
      required: false
    },
    searchTypes: {
      type: Array,
      required: true
    },
    searchItems: {
      type: Array,
      required: false,
      default: () => []
    },
    singleSearch: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultSearchType: {
      type: Object,
      required: false,
      default: () => ({})
    },
    showOperatorDropdown: {
      type: Boolean,
      required: false,
      default: false
    },
    operator: {
      type: String,
      required: false,
      default: 'AND'
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      modelSearchType: '',
      modelSearchFor: '',
      modelSearchKey: '',
      searchTags: [],
      searchOperator: this.operator,
      customSearchInput: '',
      // The key box (ddr searches) needs its own search-input model. Sharing one
      // with the value box made the unfocused box reset the other's text, blocking typing.
      customSearchKeyInput: '',
      // Remembers what the current custom search fetches (keys vs values for a key) so
      // typing and pagination keep fetching the same entity instead of falling back to keys.
      customSearchEntityType: undefined,
      customSearchKey: undefined,
      customSearchResult: {
        total: 0,
        hasMore: true,
        pages: []
      },
      customSearchResultLoading: false,
      customSearchSelectedItemCopy: null,
      customSearchPageSize: 50,
      customSearchHasMore: true,
      isIntersecting: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    disableAddSearch() {
      return !(this.modelSearchType !== '' && this.modelSearchFor !== '');
    },
    cols() {
      const hasKey = get(this.modelSearchType, 'param') === 'keyValue' || get(this.modelSearchType, 'param') === 'ddrTarget' || get(this.modelSearchType, 'param') === 'ddrDestination';
      return {
        category: {
          cols: hasKey ? 4 : 6,
          md: hasKey ? 4 : 2,
          lg: 3
        },
        keyBox: {
          cols: hasKey ? 3 : 0,
          md: hasKey ? 4 : 0,
          lg: hasKey ? 2 : 0
        },
        searchBox: {
          cols: hasKey ? 5 : 6,
          md: hasKey ? 4 : 6,
          lg: hasKey ? 3 : 4
        },
        displayBox: {
          cols: 12,
          md: hasKey ? 12 : 4,
          lg: hasKey ? 4 : 5
        }
      };
    },
    searchOperators() {
      return [{
        text: this.$i18n_t('Common.searchOperator.AND'),
        param: 'AND'
      }, {
        text: this.$i18n_t('Common.searchOperator.OR'),
        param: 'OR'
      }];
    },
    useCustomSearch() {
      if (!this.modelSearchType) {
        return false;
      }
      const searchHandlerType = typeof get(this.modelSearchType, 'searchHandler');
      return searchHandlerType === 'function';
    },
    isDdrSearch() {
      const param = get(this.modelSearchType, 'param');
      return param === 'ddrTarget' || param === 'ddrDestination';
    },
    selectedDdrKeyText() {
      // modelSearchKey is the selected key object ({ text, value }) or an empty string.
      return get(this.modelSearchKey, 'text');
    },
    customSearchResultWithSelected() {
      let items = [];
      if (this.customSearchResult.pages && this.customSearchResult.pages.length) {
        items = cloneDeep(this.customSearchResult.pages.flat());
      }
      if (this.customSearchSelectedItemCopy) {
        items.push(this.customSearchSelectedItemCopy);
      }

      // unique by id
      return items.map((item, index) => ({
        ...item,
        __doIntersect__: index >= items.length - this.customSearchPageSize / 2
      }));
    },
    itemsForSearchKey() {
      if (this.modelSearchKey && !this.customSearchResultWithSelected.find(item => item.value === this.modelSearchKey)) {
        return [this.modelSearchKey, ...this.customSearchResultWithSelected];
      }
      return this.customSearchResultWithSelected;
    },
    itemsForSearchFor() {
      if (this.modelSearchFor && !this.customSearchResultWithSelected.find(item => item.value === this.modelSearchFor)) {
        return [this.modelSearchFor, ...this.customSearchResultWithSelected];
      }
      return this.customSearchResultWithSelected;
    }
  },
  watch: {
    searchTypes() {
      const store = get(this.modelSearchType, 'param');
      this.modelSearchType = this.searchTypes.find(item => item.param === store) || {};
    },
    searchItems() {
      this.searchTags = this.searchItems;
    },
    operator() {
      this.searchOperator = this.searchOperators.filter(item => item.param === this.operator)[0] || {};
    },
    // Typing into the value box. For ddr searches this must fetch values for the
    // selected key; for every other custom search it fetches the entity itself.
    customSearchInput: {
      immediate: true,
      handler() {
        if (this.isDdrSearch) {
          // Values can only be fetched for a selected key; without one the backend
          // rejects the lookup (HTTP 500), so skip the request entirely.
          this.setSearchContext('values', this.selectedDdrKeyText);
          this.resetCustomSearch();
          if (this.selectedDdrKeyText) {
            this.executeCustomSearch();
          }
          return;
        }
        this.setSearchContext(undefined, undefined);
        this.resetCustomSearch();
        this.executeCustomSearch();
      }
    },
    // Typing into the key box (ddr searches only) fetches the available keys.
    customSearchKeyInput() {
      if (!this.isDdrSearch) {
        return;
      }
      this.setSearchContext(undefined, undefined);
      this.resetCustomSearch();
      this.executeCustomSearch();
    },
    modelSearchFor(value) {
      if (this.useCustomSearch) {
        this.customSearchSelectedItemCopy = cloneDeep(value);
      }
    },
    modelSearchType() {
      this.customSearchKeyInput = '';
      this.setSearchContext(undefined, undefined);
      this.resetCustomSearch();
      this.executeCustomSearch();
    },
    isIntersecting() {
      if (!this.customSearchResultLoading) {
        this.executeCustomSearch();
      }
    },
    customSearchResultLoading() {
      if (this.isIntersecting) {
        this.executeCustomSearch();
      }
    }
  },
  mounted() {
    this.searchTags = this.searchItems;
    this.modelSearchType = isEmpty(this.defaultSearchType) ? '' : this.defaultSearchType;
    if (this.searchOperators.length > 0) {
      this.searchOperator = this.searchOperators.filter(item => item.param === this.operator)[0] || {};
    }
  },
  methods: {
    get,
    resetCustomSearch() {
      this.customSearchResult = {
        total: 0,
        hasMore: true,
        pages: []
      };
    },
    setSearchContext(entityType, key) {
      this.customSearchEntityType = entityType;
      this.customSearchKey = key;
    },
    async executeCustomSearch() {
      if (!this.useCustomSearch) {
        return;
      }
      if (this.customSearchResultLoading) {
        return;
      }
      if (!this.customSearchResult.hasMore) {
        return;
      }
      this.customSearchResultLoading = true;
      try {
        const previouslyFetchedPages = get(this.customSearchResult, 'pages', []);
        const pageToFetch = previouslyFetchedPages.length;
        const fetchedItems = await this.modelSearchType.searchHandler(this.customSearchInput, pageToFetch, this.customSearchPageSize, this.customSearchEntityType, this.customSearchKey);
        const allPreviouslyFetchedItems = previouslyFetchedPages.flat();
        const allFetchedItems = [...allPreviouslyFetchedItems, ...fetchedItems.items];
        const hasMore = fetchedItems.totalCount > allFetchedItems.length;
        this.customSearchResult = {
          total: fetchedItems.totalCount,
          hasMore,
          pages: [...previouslyFetchedPages, fetchedItems.items]
        };
        this.isIntersecting = false;
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('Common.multisearch.customSearch.error'), 'error');
        // Stop pagination and clear the intersect flag so the isIntersecting /
        // customSearchResultLoading watchers don't retry the failed page in a tight loop.
        this.customSearchResult = {
          ...this.customSearchResult,
          hasMore: false
        };
        this.isIntersecting = false;
      } finally {
        this.customSearchResultLoading = false;
      }
    },
    updateSearchType() {
      this.modelSearchFor = '';
      this.modelSearchKey = '';
      this.customSearchKeyInput = '';
    },
    searchKeyUpEnter() {
      if (!this.disableAddSearch) {
        this.searchAdd();
      }
    },
    parentTwinSelected(parentTwin) {
      this.modelSearchFor = parentTwin ? {
        value: parentTwin.id,
        text: parentTwin.title
      } : undefined;
      this.searchAdd();
    },
    searchRemove(searchItemIndex) {
      this.searchTags.splice(searchItemIndex, 1);
      this.updateSearchType();
      this.$emit('updated', this.searchTags, this.searchOperator.param);
    },
    searchOperatorUpdate() {
      this.searchTags.splice(0, this.searchTags.length);
      this.updateSearchType();
      this.$emit('updated', this.searchTags, this.searchOperator.param);
    },
    searchAdd() {
      const type = get(this.modelSearchType, 'text');
      const param = get(this.modelSearchType, 'param');
      // make sure that >null< is supported and not written as string "null" via `${text}`.trim()
      let searchForText = (this.modelSearchFor || {}).text !== undefined ? this.modelSearchFor.text : this.modelSearchFor;
      searchForText = searchForText === null ? searchForText : `${searchForText}`.trim();
      const search = (this.modelSearchFor || {}).text ? {
        value: this.modelSearchFor.value,
        text: searchForText
      } : searchForText;

      // If the to be added search already exists in `searchTags` ignore it
      const index = this.searchTags.findIndex(item => item.param === param && item.search === search);
      if (index > -1) {
        this.updateSearchType();
        return;
      }
      let key;
      if (param === 'keyValue' || param === 'ddrTarget' || param === 'ddrDestination') {
        const searchKey = this.modelSearchKey;
        if (searchKey) {
          key = typeof searchKey.text === 'string' ? searchKey.text.trim() : String(searchKey).trim();
        } else {
          key = '';
        }
      }
      this.searchTags.push({
        type,
        param,
        search,
        key
      });
      this.updateSearchType();
      this.$emit('updated', this.searchTags, this.searchOperator.param);
    },
    async endCustomSearchIntersect(entries, observer, isIntersecting) {
      if (!isIntersecting) {
        return;
      }
      this.isIntersecting = true;
    },
    async fetchDdrValues(key) {
      if (this.isDdrSearch) {
        const keyText = get(key, 'text');
        this.setSearchContext('values', keyText);
        this.resetCustomSearch();
        // A key must be selected first; fetching values without one returns HTTP 500.
        if (keyText) {
          this.executeCustomSearch();
        }
      }
    },
    async fetchDdrKeys() {
      if (this.isDdrSearch) {
        this.setSearchContext(undefined, undefined);
        this.resetCustomSearch();
        this.executeCustomSearch();
      }
    }
  }
};